import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText, ShieldCheck, Trophy, LogIn, UserPlus, Globe, ChevronDown, Check,
  Twitter, Send, Youtube, Crown, Gift, TrendingUp, CalendarDays, Mail, Lock,
  Eye, EyeOff, ArrowRight, Loader2, Clock,
} from 'lucide-react';
import { lionLogo, whitepaperUrl, community } from './mock';
import { useLang } from './LangContext';
import BannerCarousel from './components/BannerCarousel';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import { useAuth } from './AuthContext';
import { authApi } from './api';
import { useToast } from './hooks/use-toast';
import { PRESALE_START, PRESALE_PHASES, formatCountdown } from './presaleConfig';
import { presaleApi } from './api';
import AuthModal from './components/AuthModal';
import Turnstile from './components/Turnstile';
import SupportButton from './components/SupportButton';

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
];

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

const iconFor = (name) => {
  if (name.toLowerCase().includes('twitter')) return <Twitter className="w-4 h-4" />;
  if (name.toLowerCase().includes('telegram')) return <Send className="w-4 h-4" />;
  return <Youtube className="w-4 h-4" />;
};

const LoginGate = () => {
  const { lang, setLang, t } = useLang();
  const { finalizeLogin } = useAuth();
  const { toast } = useToast();

  const [authOpen, setAuthOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [langOpen, setLangOpen] = useState(false);
  const [mode, setMode] = useState('signin'); // 'signin' | 'create'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [captchaToken, setCaptchaToken] = useState(null);
  const [backendState, setBackendState] = useState(null);

  // Countdown tick every second, with resync on tab visibility & focus.
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = setInterval(tick, 1000);
    const onVisibility = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', tick);
    };
  }, []);

  // Live server-synced presale state (auto-advancing phase + 15d phase end)
  useEffect(() => {
    let alive = true;
    const load = () => presaleApi.state().then(s => { if (alive) setBackendState(s); }).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Target = phase end if presale is live, else the fixed official launch date (UTC).
  // Use the hardcoded PRESALE_START constant to ensure the countdown is always
  // stable and does not flicker waiting for the backend response on first paint.
  const target = useMemo(() => {
    if (backendState?.presale_started && backendState?.phaseEndsAt) return new Date(backendState.phaseEndsAt);
    return PRESALE_START;
  }, [backendState]);

  const countdown = useMemo(() => formatCountdown(target, now), [target, now]);
  const isLive = !!backendState?.presale_started;
  const currentPhase = backendState?.currentPhase || 1;
  const currentPrice = backendState?.currentPrice || PRESALE_PHASES[0].price;
  const startPrice = currentPrice.toFixed(4);

  // Phase 1 end date (hardcoded) — used for the blue "Prix Actuel" card countdown,
  // identical to the one on the Presale dashboard page.
  const PHASE1_ENDS_AT = useMemo(() => new Date('2026-07-25T04:00:00Z'), []);
  const phase1Countdown = useMemo(() => {
    const diff = Math.max(0, Math.floor((PHASE1_ENDS_AT.getTime() - now.getTime()) / 1000));
    return {
      days: Math.floor(diff / 86400),
      hours: Math.floor((diff % 86400) / 3600),
      mins: Math.floor((diff % 3600) / 60),
      secs: diff % 60,
    };
  }, [PHASE1_ENDS_AT, now]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'create') {
      setAuthMode('register');
      setAuthOpen(true);
      return;
    }
    if (process.env.REACT_APP_TURNSTILE_SITE_KEY && !captchaToken) {
      toast({ title: t.captcha?.required || 'Please complete the captcha', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.login({ email, password, captchaToken });
      if (res.requires_verification) {
        setAuthMode('verify');
        setAuthOpen(true);
        toast({ title: 'Verification needed', description: 'A new code was sent to your email.' });
      } else {
        finalizeLogin(res);
        toast({ title: `Welcome back, ${res.user?.username}!` });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Login failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = () => {
    // Redirect to Emergent Google Auth. After OAuth, user comes back to the
    // current page with `#session_id=...` in the URL fragment, which App.js
    // detects and exchanges for our app JWT via /api/auth/google-callback.
    const redirect = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{
        background: 'radial-gradient(circle at 15% 20%, rgba(99,102,241,0.18), transparent 35%), radial-gradient(circle at 85% 30%, rgba(59,130,246,0.18), transparent 35%), linear-gradient(180deg, #f3f4ff 0%, #e8edff 100%)',
      }}
    >
      {/* Decorative side blobs */}
      <div className="hidden md:block absolute -left-32 top-32 w-[480px] h-[480px] rounded-full bg-blue-300/25 blur-[120px] pointer-events-none" />
      <div className="hidden md:block absolute -right-24 top-10 w-[440px] h-[440px] rounded-full bg-indigo-300/25 blur-[110px] pointer-events-none" />
      <div className="hidden md:block absolute left-1/4 bottom-0 w-[380px] h-[380px] rounded-full bg-violet-300/15 blur-[120px] pointer-events-none" />

      {/* Top language switcher */}
      <div className="absolute top-5 right-5 z-30">
        <button onClick={() => setLangOpen((v) => !v)} className="flex items-center gap-1.5 bg-white/90 border border-slate-200 rounded-full px-3 py-2 shadow-sm">
          <Globe className="w-4 h-4 text-slate-700" />
          <span className="text-[12px] font-extrabold text-slate-700">{lang.toUpperCase()}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
        </button>
        {langOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setLangOpen(false)} />
            <div className="absolute right-0 mt-2 w-32 bg-white rounded-2xl border border-slate-200 shadow-xl py-1.5 z-40">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center justify-between text-[13px] font-bold text-slate-800">
                  {l.label}
                  {lang === l.code && <Check className="w-3.5 h-3.5 text-blue-600" />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="relative z-10 max-w-[760px] mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-10 flex flex-col items-center text-center">
        {/* Logo + Brand — clean, no rigid frame */}
        <div className="flex items-center gap-4 mb-3" data-testid="brand-header">
          <img
            src={lionLogo}
            alt="DOMINO ARENA"
            className="w-[88px] h-[88px] sm:w-[110px] sm:h-[110px] object-contain drop-shadow-[0_10px_30px_rgba(30,64,175,0.35)]"
          />
          <div className="text-left leading-none">
            <div className="text-[28px] sm:text-[36px] font-black text-slate-900 tracking-tight">DOMINO</div>
            <div className="text-[28px] sm:text-[36px] font-black -mt-1 bg-gradient-to-r from-blue-700 to-blue-400 bg-clip-text text-transparent tracking-tight">ARENA</div>
            <div className="text-[10px] sm:text-[11px] font-extrabold tracking-[0.35em] text-slate-500 mt-1">PLAY · WIN · EARN</div>
          </div>
        </div>

        {/* EXCLUSIVE EARLY ACCESS pill */}
        <div className="inline-flex items-center gap-2 bg-white border border-blue-300 rounded-full px-5 py-2 shadow-[0_4px_14px_rgba(59,130,246,0.15)] mt-3" data-testid="early-access-badge">
          <Crown className="w-4 h-4 text-blue-600" />
          <span className="text-[11px] sm:text-[12px] font-black tracking-[0.18em] text-blue-700">{t.loginGate?.tagline || 'EXCLUSIVE EARLY ACCESS'}</span>
        </div>

        {/* Title */}
        <h1 className="mt-5 font-black leading-[1] tracking-tight text-slate-900" style={{ fontSize: 'clamp(34px, 6vw, 60px)' }}>
          {t.loginGate?.title1 || 'PLAY. WIN.'}<br />
          {t.loginGate?.title2 || 'BECOME A'} <span className="bg-gradient-to-r from-blue-700 to-blue-400 bg-clip-text text-transparent">{t.loginGate?.title3 || 'LEGEND'}</span>
        </h1>

        <p className="mt-4 text-slate-700 text-[15px] sm:text-[16px]">
          {t.loginGate?.subtitle || 'The #1 Game Platform on'} <span className="text-blue-600 font-bold">Solana</span>
        </p>
        <p className="mt-3 text-slate-500 text-[13px] sm:text-[14px] max-w-[520px]">
          {t.loginGate?.description}
        </p>

        {/* BANNER CAROUSEL — 16:9, contain, 100% width, with purple glow underneath (matches flyer palette) */}
        <div className="w-full mt-6 relative">
          <div
            aria-hidden
            className="absolute inset-x-6 -bottom-3 h-10 rounded-full blur-2xl pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.65) 0%, rgba(139, 92, 246, 0.2) 55%, transparent 80%)' }}
          />
          <BannerCarousel aspectRatio="16 / 9" objectFit="contain" />
        </div>

        {/* PRIX ACTUEL — identical block as the Presale dashboard page */}
        <div
          className="w-full mt-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 text-white px-5 py-5 sm:py-4 shadow-[0_10px_28px_rgba(37,99,235,0.4)] relative overflow-hidden"
          data-testid="price-block-current"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Clock className="w-5 h-5 text-amber-300 shrink-0" />
              <span className="text-[11px] sm:text-[10px] font-black tracking-[0.16em] text-amber-200">SE TERMINE DANS</span>
              <span className="text-[17px] sm:text-[15px] font-black tabular-nums text-amber-100" data-testid="phase1-countdown">
                {String(phase1Countdown.days).padStart(2, '0')}j&nbsp;
                {String(phase1Countdown.hours).padStart(2, '0')}h&nbsp;
                {String(phase1Countdown.mins).padStart(2, '0')}m&nbsp;
                {String(phase1Countdown.secs).padStart(2, '0')}s
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold tracking-[0.14em] text-blue-100">PRIX ACTUEL</span>
              <span className="text-[26px] sm:text-[24px] font-black leading-none">${startPrice}</span>
              <span className="text-[10px] font-extrabold tracking-widest text-emerald-100 bg-emerald-500/30 border border-emerald-300/40 rounded-full px-2 py-0.5">EN COURS</span>
            </div>
          </div>
        </div>

        {/* CREATE ACCOUNT — single full-width CTA (Sign In is the bottom button) */}
        <div className="w-full mt-6" data-testid="mode-toggle">
          <button
            onClick={() => { setAuthMode('register'); setAuthOpen(true); }}
            data-testid="create-tab-btn"
            className="w-full flex items-center justify-center gap-3 rounded-2xl py-4 font-black text-[15px] tracking-wider transition-all bg-gradient-to-r from-blue-700 to-blue-500 text-white shadow-[0_10px_24px_rgba(37,99,235,0.45)] hover:-translate-y-0.5"
          >
            <UserPlus className="w-5 h-5" /> {(t.loginGate?.createAccount || 'Create Account').toUpperCase()}
          </button>
        </div>

        {/* GOOGLE BUTTON */}
        <button
          onClick={handleGoogleClick}
          data-testid="google-signin-btn"
          className="w-full mt-3 flex items-center justify-center gap-3 bg-white rounded-2xl py-3.5 border border-slate-200 hover:border-blue-300 shadow-sm transition-all hover:-translate-y-0.5"
        >
          <GoogleIcon />
          <span className="text-[14px] font-extrabold text-slate-800">{t.loginGate?.continueWithGoogle || 'Continue with Google'}</span>
        </button>

        {/* DIVIDER */}
        <div className="w-full mt-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-[10px] sm:text-[11px] font-extrabold tracking-[0.18em] text-slate-400">{t.loginGate?.orContinueEmail || 'OR CONTINUE WITH EMAIL'}</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {/* EMAIL/PASSWORD FORM */}
        <form onSubmit={handleSubmit} className="w-full mt-4 flex flex-col gap-3" data-testid="auth-form">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.loginGate?.emailPlaceholder || 'Email address'}
              data-testid="email-input"
              className="w-full bg-white border border-slate-200 rounded-2xl py-3.5 pl-11 pr-4 text-[14px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
            <input
              type={showPwd ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="password-input"
              className="w-full bg-white border border-slate-200 rounded-2xl py-3.5 pl-11 pr-11 text-[14px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
            />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex items-center justify-between text-[12px] mt-1 px-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
              />
              <span className="text-slate-600">{t.loginGate?.rememberMe || 'Remember me'}</span>
            </label>
            <button type="button" onClick={() => setForgotOpen(true)} className="text-blue-600 font-bold hover:underline" data-testid="forgot-password-link">
              {t.loginGate?.forgotPassword || 'Forgot password?'}
            </button>
          </div>

          <div className="mt-2"><Turnstile onToken={setCaptchaToken} action="login" /></div>

          <button
            type="submit"
            disabled={loading}
            data-testid="submit-auth-btn"
            className="w-full mt-2 flex items-center justify-center gap-3 rounded-2xl py-3.5 font-black text-white text-[14px] tracking-wider shadow-[0_10px_24px_rgba(37,99,235,0.45)] disabled:opacity-60 transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === 'create' ? (t.loginGate?.createAccount || 'Create Account').toUpperCase() : (t.loginGate?.signIn || 'Sign In').toUpperCase()} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        {/* WHITEPAPER LINK */}
        <button onClick={() => window.open(whitepaperUrl, '_blank')} className="mt-5 flex items-center gap-2 text-blue-600 font-bold text-[13px] hover:underline">
          <FileText className="w-4 h-4" /> {t.loginGate?.readWhitepaper || 'Read the whitepaper'}
        </button>

        {/* 3 BADGES */}
        <div className="w-full mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
          <BadgeCard
            icon={<ShieldCheck className="w-7 h-7 text-blue-600" />}
            title={(t.loginGate?.secure || 'SECURITY').toUpperCase()}
            desc={t.loginGate?.securityDesc}
          />
          <BadgeCard
            icon={<Trophy className="w-7 h-7 text-blue-600" />}
            title={(t.loginGate?.pte || 'PLAY TO EARN').toUpperCase()}
            desc={t.loginGate?.pteDesc}
          />
          <BadgeCard
            icon={<ShieldCheck className="w-7 h-7 text-blue-600" />}
            title={(t.loginGate?.audited || 'VERIFIED').toUpperCase()}
            desc={t.loginGate?.auditedDesc}
          />
        </div>

        {/* SOCIAL */}
        <div className="w-full mt-8">
          <div className="text-[11px] font-extrabold tracking-[0.22em] text-slate-500 mb-4">{t.loginGate?.followSocial || 'FOLLOW US ON SOCIAL MEDIA'}</div>
          <div className="flex items-center justify-center gap-3" data-testid="social-bar">
            {community.map((c, i) => (
              <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white border border-slate-200 shadow-sm hover:border-blue-300 hover:-translate-y-0.5 transition-all flex items-center justify-center text-slate-700">
                {iconFor(c.name)}
              </a>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div className="w-full mt-10 flex flex-col sm:flex-row items-center justify-between gap-3 pt-5 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 overflow-hidden flex items-center justify-center">
              <img src={lionLogo} alt="" className="w-[200%]" style={{ mixBlendMode: 'screen' }} />
            </div>
            <span className="text-[11px] text-slate-500">{t.loginGate?.footerCopy || '© 2026 Domino Arena. All rights reserved.'}</span>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-slate-600 font-bold flex-wrap justify-center">
            <a href="#" className="hover:text-blue-600">{t.loginGate?.terms || 'Terms & Conditions'}</a>
            <a href="#" className="hover:text-blue-600">{t.loginGate?.privacy || 'Privacy Policy'}</a>
            <a href="#" className="hover:text-blue-600">{t.loginGate?.disclaimer || 'Disclaimer'}</a>
            <a href="#" className="hover:text-blue-600">{t.loginGate?.contact || 'Contact Us'}</a>
          </div>
        </div>
      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
      <SupportButton />
    </div>
  );
};

const TimeUnit = ({ value, label, sep }) => (
  <div className="relative flex flex-col items-center">
    {sep && <div className="absolute -left-1 sm:-left-2 top-3 text-2xl sm:text-3xl text-slate-300 font-black select-none">|</div>}
    <div className="text-[36px] sm:text-[52px] font-black text-slate-900 leading-none">{String(value).padStart(2, '0')}</div>
    <div className="text-[10px] sm:text-[11px] font-extrabold tracking-[0.18em] text-slate-500 mt-1">{label}</div>
  </div>
);

const BadgeCard = ({ icon, title, desc }) => (
  <div className="flex items-start gap-3 bg-white/85 backdrop-blur-sm border border-slate-200 rounded-2xl p-4">
    <div className="shrink-0">{icon}</div>
    <div className="leading-tight">
      <div className="text-[12px] font-black text-slate-900 tracking-[0.12em]">{title}</div>
      <div className="text-[11px] text-slate-500 mt-1">{desc}</div>
    </div>
  </div>
);

export default LoginGate;
